// =============================================================================
// Guide chrome hydration — reads the progress store + topics tree and fills:
//   - left nav % labels ([data-aa-pct])
//   - right rail Setup progress card ([data-aa-setup]) for type-B topics:
//     a grand step is done when all its sub-step leaves are >= 95
//   - left nav accordion (one topic open at a time)
// Re-runs on every "aa-progress" event so state tracks live scrolling.
// =============================================================================

import { loadStore, isDone, statusLabel } from "./progress-store.js";
import { readTree, activeTopic } from "./guide-data.js";

const leafSlugs = (step) =>
	(step.substeps || []).length
		? step.substeps.filter((s) => s.slug).map((s) => s.slug)
		: step.slug
		? [step.slug]
		: [];

function hydrate() {
	const tree = readTree();
	if (!tree) {
		return;
	}
	const { progress } = loadStore();

	// Left nav percentage labels.
	document.querySelectorAll("[data-aa-pct]").forEach((el) => {
		const pct = progress[el.dataset.aaPct] || 0;
		el.textContent = statusLabel(pct);
		el.classList.toggle("is-done", isDone(pct));
	});

	// Hub panels: progress bar fill + status label per child guide.
	document.querySelectorAll("[data-aa-hub-fill]").forEach((el) => {
		el.style.width = `${progress[el.dataset.aaHubFill] || 0}%`;
	});
	document.querySelectorAll("[data-aa-hub-status]").forEach((el) => {
		const pct = progress[el.dataset.aaHubStatus] || 0;
		el.textContent = isDone(pct) ? "Done" : pct > 0 ? `${pct}% read` : "Not started";
	});

	// Setup progress card (only rendered for type-B topics).
	const card = document.querySelector("[data-aa-setup]");
	const topic = activeTopic();
	if (!card || !topic) {
		return;
	}

	let doneCount = 0;
	(topic.steps || []).forEach((step) => {
		const slugs = leafSlugs(step);
		const done =
			slugs.length > 0 && slugs.every((s) => isDone(progress[s] || 0));
		if (done) {
			doneCount++;
		}
		const el = card.querySelector(
			`[data-aa-setup-step="${CSS.escape(step.id)}"]`
		);
		if (el) {
			el.classList.toggle("is-done", done);
		}
	});

	const total = (topic.steps || []).length;
	const pct = total ? Math.round((doneCount / total) * 100) : 0;
	const pctEl = card.querySelector("[data-aa-setup-pct]");
	const countEl = card.querySelector("[data-aa-setup-count]");
	const fillEl = card.querySelector("[data-aa-setup-fill]");
	if (pctEl) {
		pctEl.textContent = `${pct}%`;
	}
	if (countEl) {
		countEl.textContent = `${doneCount} of ${total} steps done`;
	}
	if (fillEl) {
		fillEl.style.width = `${pct}%`;
	}
}

function initAccordion() {
	document.querySelectorAll("[data-aa-nav-topic]").forEach((topicEl) => {
		const head = topicEl.querySelector(
			".aa-article-nav__group-head, .aa-article-nav__start-row"
		);
		if (!head) {
			return;
		}
		head.addEventListener("click", () => {
			const wasOpen = topicEl.classList.contains("is-open");
			document
				.querySelectorAll("[data-aa-nav-topic].is-open")
				.forEach((el) => {
					el.classList.remove("is-open");
					const toggle = el.querySelector(".aa-article-nav__toggle");
					if (toggle) {
						toggle.textContent = "+";
					}
				});
			if (!wasOpen) {
				topicEl.classList.add("is-open");
				const toggle = topicEl.querySelector(".aa-article-nav__toggle");
				if (toggle) {
					toggle.textContent = "−";
				}
			}
		});
	});
}

function initHubPanels() {
	document.querySelectorAll(".aa-hub-panel").forEach((panel) => {
		const head = panel.querySelector(".aa-hub-panel__head");
		const sign = panel.querySelector(".aa-hub-panel__sign");
		if (!head || !sign) {
			return;
		}
		head.addEventListener("click", () => {
			const wasOpen = panel.classList.contains("is-open");
			// One panel open at a time.
			document.querySelectorAll(".aa-hub-panel.is-open").forEach((el) => {
				el.classList.remove("is-open");
				const s = el.querySelector(".aa-hub-panel__sign");
				if (s) {
					s.textContent = "+";
				}
			});
			if (!wasOpen) {
				panel.classList.add("is-open");
				sign.textContent = "−";
			}
		});
	});
}

document.addEventListener("DOMContentLoaded", () => {
	hydrate();
	initAccordion();
	initHubPanels();
});
window.addEventListener("aa-progress", hydrate);
